/**
 * ISHVARI WEBSITE V3 — GLOBAL FOUNDATION SCRIPT
 * ---------------------------------------------------------------------
 * Minimal, dependency-free JavaScript shared by every section on the
 * site (Bible §6.14 Performance Standards — minimal JS). This file
 * only wires up foundation-level behavior:
 *
 *   1. Scroll-reveal for any [data-animate] element (IntersectionObserver)
 *   2. Sticky-element state class (.is-stuck) for headers/bars
 *   3. Respect for prefers-reduced-motion and the merchant's
 *      "Enable animations" Theme Setting
 *
 * Section-specific behavior (mega menu, cart drawer, product gallery,
 * etc.) belongs in that section's own scoped script, loaded only on
 * the templates that need it — not here.
 * ===================================================================== */

(function () {
  "use strict";

  var docEl = document.documentElement;

  var prefersReducedMotion =
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  var animationsDisabled =
    docEl.getAttribute("data-animations") === "off" || prefersReducedMotion;

  /* -----------------------------------------------------------
     1. Scroll reveal
     Any element marked data-animate (typically combined with a
     class like fade-in-up / slide-in-left / scale-in from
     animations.css) becomes visible once it enters the viewport.
     If animations are disabled, everything is shown immediately —
     content must never be hidden because JS/animation is off.
  ----------------------------------------------------------- */
  function initScrollReveal() {
    var targets = document.querySelectorAll("[data-animate]");
    if (!targets.length) return;

    if (animationsDisabled || !("IntersectionObserver" in window)) {
      targets.forEach(function (el) {
        el.classList.add("is-visible");
      });
      return;
    }

    targets.forEach(function (el, i) {
      if (el.hasAttribute("data-animate-index")) {
        el.style.setProperty("--animate-index", el.getAttribute("data-animate-index"));
      } else if (el.closest("[data-animate-group]")) {
        el.style.setProperty("--animate-index", i);
      }
    });

    var observer = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            obs.unobserve(entry.target);
          }
        });
      },
      { root: null, rootMargin: "0px 0px -10% 0px", threshold: 0.15 }
    );

    targets.forEach(function (el) {
      observer.observe(el);
    });
  }

  /* -----------------------------------------------------------
     2. Sticky element state
     Elements with class .sticky-el receive .is-stuck once scrolled
     past their natural position, so a shadow/background can be
     applied only while actually pinned (see animations.css).
     Uses a 1px sentinel above the element — no scroll-event
     listeners, no layout thrashing.
  ----------------------------------------------------------- */
  function initStickyState() {
    var stickies = document.querySelectorAll(".sticky-el");
    if (!stickies.length || !("IntersectionObserver" in window)) return;

    stickies.forEach(function (el) {
      var sentinel = document.createElement("div");
      sentinel.setAttribute("aria-hidden", "true");
      sentinel.style.position = "absolute";
      sentinel.style.top = "0";
      sentinel.style.height = "1px";
      sentinel.style.width = "1px";
      sentinel.style.pointerEvents = "none";
      el.style.position = el.style.position || "sticky";
      el.parentNode.insertBefore(sentinel, el);

      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            el.classList.toggle("is-stuck", !entry.isIntersecting);
          });
        },
        { threshold: 0 }
      );
      observer.observe(sentinel);
    });
  }

  /* -----------------------------------------------------------
     3. Boot
  ----------------------------------------------------------- */
  function init() {
    initScrollReveal();
    initStickyState();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  /* Expose a tiny namespace so future section scripts can reuse the
     reduced-motion/disabled-animation check without recomputing it. */
  window.Ishvari = window.Ishvari || {};
  window.Ishvari.animationsDisabled = animationsDisabled;
})();
